import { useState, useMemo, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  AlertTriangle,
  AlertCircle,
  ShieldAlert,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Filter,
  CheckCircle,
  Globe,
  Link2,
  RefreshCw,
  LinkIcon,
  Download,
  Square,
  CheckSquare,
} from 'lucide-react';
import { LIST_TOXIC_BACKLINKS } from '../../graphql/queries';
import { ANALYZE_BACKLINKS } from '../../graphql/mutations';
import type { ToxicBacklink, ToxicBacklinksSummary, ToxicityFlag } from '../../types';

const client = generateClient();

interface ToxicBacklinksTabProps {
  scanId: string;
  siteId: string;
  gscConnected?: boolean;
}

type RiskLevel = 'high' | 'medium' | 'low';
type GroupBy = 'risk' | 'flag' | 'domain' | 'none';
type DisavowMode = 'url' | 'domain';

// Flag configuration with descriptions and colors (same as outbound)
const FLAG_CONFIG: Record<ToxicityFlag, { label: string; description: string; color: string; bgColor: string }> = {
  SUSPICIOUS_TLD: {
    label: 'Suspicious TLD',
    description: 'Domain uses a TLD commonly associated with spam or malicious sites',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
  },
  DEAD_LINK: {
    label: 'Dead Link',
    description: 'Link returns an error status or is unreachable',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  REDIRECT_CHAIN: {
    label: 'Redirect Chain',
    description: 'Link redirects through multiple URLs before reaching final destination',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
  },
  SUSPICIOUS_PATTERN: {
    label: 'Suspicious Pattern',
    description: 'URL contains patterns commonly associated with spam or malicious content',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  PARKED_DOMAIN: {
    label: 'Parked Domain',
    description: 'Domain appears to be parked or inactive',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
  },
  ADULT_CONTENT: {
    label: 'Adult Content',
    description: 'Domain may contain adult content',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
  },
  GAMBLING: {
    label: 'Gambling',
    description: 'Domain appears to be related to gambling',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  PHARMACY: {
    label: 'Pharmacy',
    description: 'Domain may be associated with unregulated pharmaceutical sales',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
};

function getRiskLevel(score: number): RiskLevel {
  if (score >= 70) return 'high';
  if (score >= 30) return 'medium';
  return 'low';
}

function getRiskConfig(level: RiskLevel) {
  switch (level) {
    case 'high':
      return { label: 'High Risk', color: 'text-red-700', bgColor: 'bg-red-100', borderColor: 'border-red-200' };
    case 'medium':
      return { label: 'Medium Risk', color: 'text-orange-700', bgColor: 'bg-orange-100', borderColor: 'border-orange-200' };
    case 'low':
      return { label: 'Low Risk', color: 'text-yellow-700', bgColor: 'bg-yellow-100', borderColor: 'border-yellow-200' };
  }
}

function truncateUrl(url: string, maxLength = 60): string {
  if (url.length <= maxLength) return url;
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname + urlObj.search;
    if (path.length <= maxLength) return path;
    return '...' + path.slice(-maxLength);
  } catch {
    return '...' + url.slice(-maxLength);
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 hover:bg-gray-200 rounded"
      aria-label={copied ? 'Copied!' : 'Copy URL'}
    >
      {copied ? (
        <Check size={14} className="text-status-success" />
      ) : (
        <Copy size={14} className="text-gray-400" />
      )}
    </button>
  );
}

interface BacklinkCardProps {
  backlink: ToxicBacklink;
  isSelected: boolean;
  onToggle: (url: string) => void;
}

function BacklinkCard({ backlink, isSelected, onToggle }: BacklinkCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const riskLevel = getRiskLevel(backlink.toxicityScore);
  const riskConfig = getRiskConfig(riskLevel);

  return (
    <div className={`border ${isSelected ? 'border-coral bg-coral/5' : riskConfig.borderColor} rounded-lg overflow-hidden bg-white transition-colors`}>
      <div className="flex items-stretch">
        {/* Checkbox column */}
        <button
          onClick={() => onToggle(backlink.linkingUrl)}
          className="flex items-start pt-4 px-3 hover:bg-gray-light/30 transition-colors flex-shrink-0"
          aria-label={isSelected ? 'Deselect' : 'Select for disavow'}
          title={isSelected ? 'Deselect' : 'Select for disavow'}
        >
          {isSelected ? (
            <CheckSquare size={18} className="text-coral" />
          ) : (
            <Square size={18} className="text-gray-300" />
          )}
        </button>

        {/* Main card content */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex items-start gap-4 p-4 hover:bg-gray-light/30 transition-colors text-left min-w-0"
          aria-expanded={isExpanded}
        >
          <div className="flex-1 min-w-0">
            {/* Risk Level and Score */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded text-caption font-medium ${riskConfig.bgColor} ${riskConfig.color}`}>
                {riskConfig.label} ({backlink.toxicityScore})
              </span>
              {backlink.statusCode && backlink.statusCode >= 400 && (
                <span className="px-2 py-0.5 rounded text-caption bg-red-100 text-red-700">
                  HTTP {backlink.statusCode}
                </span>
              )}
            </div>

            {/* Linking URL */}
            <div className="flex items-center gap-2">
              <LinkIcon size={16} className={riskConfig.color} />
              <span className="text-body font-medium text-dark-text truncate" title={backlink.linkingUrl}>
                {truncateUrl(backlink.linkingUrl)}
              </span>
            </div>

            {/* Domain */}
            <p className="text-body-sm text-gray-medium mt-1">
              Domain: <span className="font-medium">{backlink.linkingDomain}</span>
            </p>

            {/* Target URL on your site */}
            <p className="text-body-sm text-gray-medium mt-1">
              Links to: <span className="font-medium">{truncateUrl(backlink.targetUrl, 40)}</span>
            </p>

            {/* Anchor Text */}
            {backlink.anchorText && (
              <p className="text-body-sm text-gray-medium mt-1 line-clamp-1">
                Anchor: "{backlink.anchorText}"
              </p>
            )}

            {/* Flags */}
            <div className="flex flex-wrap gap-1 mt-2">
              {backlink.flags.map((flag) => {
                const config = FLAG_CONFIG[flag];
                return (
                  <span
                    key={flag}
                    className={`px-2 py-0.5 rounded text-caption font-medium ${config.bgColor} ${config.color}`}
                    title={config.description}
                  >
                    {config.label}
                  </span>
                );
              })}
            </div>
          </div>

          {isExpanded ? (
            <ChevronDown size={20} className="text-gray-medium flex-shrink-0 mt-1" />
          ) : (
            <ChevronRight size={20} className="text-gray-medium flex-shrink-0 mt-1" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-light p-4 bg-gray-light/20 space-y-3">
          {/* Full Linking URL */}
          <div>
            <span className="text-caption text-gray-medium block mb-1">Linking URL (external site)</span>
            <div className="flex items-center gap-2 bg-white p-2 rounded border border-gray-200">
              <a
                href={backlink.linkingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-sm text-coral hover:underline truncate flex-1"
              >
                {backlink.linkingUrl}
              </a>
              <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
              <CopyButton text={backlink.linkingUrl} />
            </div>
          </div>

          {/* Target URL */}
          <div>
            <span className="text-caption text-gray-medium block mb-1">Target URL (your site)</span>
            <div className="flex items-center gap-2 bg-white p-2 rounded border border-gray-200">
              <a
                href={backlink.targetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-sm text-coral hover:underline truncate flex-1"
              >
                {backlink.targetUrl}
              </a>
              <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
              <CopyButton text={backlink.targetUrl} />
            </div>
          </div>

          {/* Anchor Text */}
          {backlink.anchorText && (
            <div>
              <span className="text-caption text-gray-medium block mb-1">Anchor Text</span>
              <p className="text-body-sm bg-white p-2 rounded border border-gray-200">
                {backlink.anchorText || <span className="text-gray-400 italic">No text</span>}
              </p>
            </div>
          )}

          {/* Redirect Chain */}
          {backlink.redirectChain && backlink.redirectChain.length > 0 && (
            <div>
              <span className="text-caption text-gray-medium block mb-1">Redirect Chain</span>
              <div className="bg-white p-2 rounded border border-gray-200 space-y-1">
                {backlink.redirectChain.map((url, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-body-sm">
                    <span className="text-gray-400">{idx + 1}.</span>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-coral hover:underline truncate"
                    >
                      {truncateUrl(url, 50)}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flag Details */}
          <div>
            <span className="text-caption text-gray-medium block mb-1">Toxicity Flags</span>
            <div className="bg-white p-2 rounded border border-gray-200 space-y-2">
              {backlink.flags.map((flag) => {
                const config = FLAG_CONFIG[flag];
                return (
                  <div key={flag} className="flex items-start gap-2">
                    <span className={`px-2 py-0.5 rounded text-caption font-medium ${config.bgColor} ${config.color}`}>
                      {config.label}
                    </span>
                    <span className="text-body-sm text-gray-medium">{config.description}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Analyzed At */}
          <div className="text-caption text-gray-medium">
            Analyzed: {new Date(backlink.analyzedAt).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="border border-gray-light rounded-lg p-4 animate-pulse">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-5 w-24 bg-gray-light rounded" />
        <div className="h-5 w-16 bg-gray-light rounded" />
      </div>
      <div className="h-4 w-3/4 bg-gray-light rounded mb-2" />
      <div className="h-3 w-1/2 bg-gray-light rounded" />
    </div>
  );
}

export function ToxicBacklinksTab({ scanId, siteId, gscConnected = false }: ToxicBacklinksTabProps) {
  const [groupBy, setGroupBy] = useState<GroupBy>('risk');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backlinks, setBacklinks] = useState<ToxicBacklink[]>([]);
  const [summary, setSummary] = useState<ToxicBacklinksSummary | null>(null);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [disavowMode, setDisavowMode] = useState<DisavowMode>('domain');

  // Fetch backlinks on mount
  useEffect(() => {
    fetchBacklinks();
  }, [scanId]);

  const fetchBacklinks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await client.graphql({
        query: LIST_TOXIC_BACKLINKS,
        variables: { scanId, limit: 100 },
      }) as { data: { listToxicBacklinks: { items: ToxicBacklink[]; summary?: ToxicBacklinksSummary } } };

      setBacklinks(response.data.listToxicBacklinks.items);
      setSummary(response.data.listToxicBacklinks.summary || null);
    } catch (err) {
      console.error('Error fetching backlinks:', err);
      setError('Failed to load backlink data');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeBacklinks = async () => {
    setAnalyzing(true);
    setError(null);
    try {
      const response = await client.graphql({
        query: ANALYZE_BACKLINKS,
        variables: { siteId, scanId },
      }) as { data: { analyzeBacklinks: ToxicBacklinksSummary } };

      setSummary(response.data.analyzeBacklinks);
      // Refresh backlinks list
      await fetchBacklinks();
    } catch (err) {
      console.error('Error analyzing backlinks:', err);
      setError('Failed to analyze backlinks. Make sure Google Search Console is connected.');
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleUrl = useCallback((url: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  }, []);

  const selectAll = () => setSelectedUrls(new Set(backlinks.map((b) => b.linkingUrl)));
  const deselectAll = () => setSelectedUrls(new Set());
  const selectHighRisk = () =>
    setSelectedUrls(new Set(backlinks.filter((b) => getRiskLevel(b.toxicityScore) === 'high').map((b) => b.linkingUrl)));

  const selectedBacklinks = useMemo(
    () => backlinks.filter((b) => selectedUrls.has(b.linkingUrl)),
    [backlinks, selectedUrls]
  );

  const downloadDisavowFile = () => {
    const date = new Date().toISOString().split('T')[0];
    const lines: string[] = [
      `# Disavow file generated by Website Compliance Scanner`,
      `# Date: ${date}`,
      `# Links selected: ${selectedBacklinks.length}`,
      `#`,
    ];

    if (disavowMode === 'domain') {
      const domains = [...new Set(selectedBacklinks.map((b) => b.linkingDomain))];
      lines.push(`# Disavowing ${domains.length} unique domain(s)`, '');
      for (const domain of domains.sort()) {
        lines.push(`domain:${domain}`);
      }
    } else {
      lines.push(`# Disavowing ${selectedBacklinks.length} specific URL(s)`, '');
      for (const link of selectedBacklinks) {
        lines.push(link.linkingUrl);
      }
    }

    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `disavow-${date}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Summary counts
  const riskCounts = useMemo(() => {
    if (summary) {
      return {
        high: summary.highRisk,
        medium: summary.mediumRisk,
        low: summary.lowRisk,
        total: summary.total,
        uniqueDomains: summary.uniqueDomains,
      };
    }
    return backlinks.reduce(
      (acc, link) => {
        const level = getRiskLevel(link.toxicityScore);
        acc[level]++;
        acc.total++;
        return acc;
      },
      { high: 0, medium: 0, low: 0, total: 0, uniqueDomains: 0 }
    );
  }, [backlinks, summary]);

  // Count by flag type
  const flagCounts = useMemo(() => {
    const counts: Partial<Record<ToxicityFlag, number>> = {};
    for (const link of backlinks) {
      for (const flag of link.flags) {
        counts[flag] = (counts[flag] || 0) + 1;
      }
    }
    return counts;
  }, [backlinks]);

  // Group by risk level
  const linksByRisk = useMemo(() => {
    const groups: Record<RiskLevel, ToxicBacklink[]> = { high: [], medium: [], low: [] };
    for (const link of backlinks) {
      const level = getRiskLevel(link.toxicityScore);
      groups[level].push(link);
    }
    return groups;
  }, [backlinks]);

  // Group by flag
  const linksByFlag = useMemo(() => {
    const groups = new Map<ToxicityFlag, ToxicBacklink[]>();
    for (const link of backlinks) {
      for (const flag of link.flags) {
        const existing = groups.get(flag) || [];
        groups.set(flag, [...existing, link]);
      }
    }
    return groups;
  }, [backlinks]);

  // Group by domain
  const linksByDomain = useMemo(() => {
    const groups = new Map<string, ToxicBacklink[]>();
    for (const link of backlinks) {
      const existing = groups.get(link.linkingDomain) || [];
      groups.set(link.linkingDomain, [...existing, link]);
    }
    return new Map([...groups.entries()].sort((a, b) => b[1].length - a[1].length));
  }, [backlinks]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="card">
          <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
            <ShieldAlert className="text-coral" size={24} />
            Toxic Backlinks (Inbound)
          </h3>
          <div className="animate-pulse">
            <div className="h-20 bg-gray-light rounded mb-4" />
            <div className="grid grid-cols-3 gap-4">
              <div className="h-16 bg-gray-light rounded" />
              <div className="h-16 bg-gray-light rounded" />
              <div className="h-16 bg-gray-light rounded" />
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-heading-3 font-semibold mb-4">All Toxic Backlinks</h3>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Summary */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-heading-3 font-semibold flex items-center gap-2">
            <ShieldAlert className="text-coral" size={24} />
            Toxic Backlinks (Inbound)
          </h3>
          {gscConnected && (
            <button
              onClick={handleAnalyzeBacklinks}
              disabled={analyzing}
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <RefreshCw size={14} className={analyzing ? 'animate-spin' : ''} />
              {analyzing ? 'Analyzing...' : 'Analyze Now'}
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200">
            {error}
          </div>
        )}

        {!gscConnected ? (
          <div className="text-center py-8">
            <Link2 size={48} className="mx-auto text-gray-300 mb-4" />
            <h4 className="text-heading-3 font-semibold text-dark-text mb-2">
              Google Search Console Not Connected
            </h4>
            <p className="text-body text-gray-medium mb-4">
              Connect Google Search Console in site settings to analyze inbound backlinks for toxicity.
            </p>
          </div>
        ) : backlinks.length > 0 ? (
          <>
            {/* Total count */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <AlertTriangle size={24} className="text-status-warning" />
                <span className="text-heading-1 font-bold text-status-warning">{riskCounts.total}</span>
                <span className="text-body text-gray-medium">toxic backlinks found</span>
              </div>
              {riskCounts.uniqueDomains > 0 && (
                <span className="text-body-sm text-gray-medium">
                  from {riskCounts.uniqueDomains} unique domains
                </span>
              )}
            </div>

            {/* Risk Breakdown */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle size={18} className="text-red-600" />
                  <span className="text-heading-3 font-bold text-red-800">{riskCounts.high}</span>
                </div>
                <div className="text-body-sm text-gray-medium">High Risk (70+)</div>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={18} className="text-orange-600" />
                  <span className="text-heading-3 font-bold text-orange-800">{riskCounts.medium}</span>
                </div>
                <div className="text-body-sm text-gray-medium">Medium Risk (30-69)</div>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={18} className="text-yellow-600" />
                  <span className="text-heading-3 font-bold text-yellow-800">{riskCounts.low}</span>
                </div>
                <div className="text-body-sm text-gray-medium">Low Risk (1-29)</div>
              </div>
            </div>

            {/* Flag breakdown */}
            {Object.keys(flagCounts).length > 0 && (
              <div className="border-t border-gray-light pt-4">
                <h4 className="text-body font-semibold text-gray-medium mb-3">Issues Detected</h4>
                <div className="flex flex-wrap gap-2">
                  {(Object.entries(flagCounts) as [ToxicityFlag, number][])
                    .sort((a, b) => b[1] - a[1])
                    .map(([flag, count]) => {
                      const config = FLAG_CONFIG[flag];
                      return (
                        <span
                          key={flag}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-caption font-medium ${config.bgColor} ${config.color}`}
                        >
                          {config.label}
                          <span className="bg-white/50 px-1.5 rounded">{count}</span>
                        </span>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Info box */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100 text-body-sm text-blue-800">
              <div className="flex items-start gap-2">
                <Globe size={16} className="flex-shrink-0 mt-0.5" />
                <p>
                  Toxic backlinks are inbound links from suspicious, spammy, or potentially harmful domains pointing to your website.
                  These can negatively impact your SEO rankings and may indicate negative SEO attacks or past link building issues.
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <CheckCircle size={48} className="mx-auto text-status-success opacity-50 mb-4" />
            <h4 className="text-heading-3 font-semibold text-dark-text mb-2">
              No Toxic Backlinks Found
            </h4>
            <p className="text-body text-gray-medium mb-4">
              No potentially harmful inbound backlinks were detected.
            </p>
            <button
              onClick={handleAnalyzeBacklinks}
              disabled={analyzing}
              className="btn-primary text-sm"
            >
              {analyzing ? 'Analyzing...' : 'Run Analysis'}
            </button>
          </div>
        )}
      </div>

      {/* Links List */}
      {backlinks.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-heading-3 font-semibold flex items-center gap-2">
              <Link2 className="text-coral" size={24} />
              All Toxic Backlinks
            </h3>

            {/* Group By Selector */}
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-gray-medium" />
              <span className="text-body-sm text-gray-medium">Group by:</span>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-body-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
              >
                <option value="risk">Risk Level</option>
                <option value="flag">Issue Type</option>
                <option value="domain">Linking Domain</option>
                <option value="none">No Grouping</option>
              </select>
            </div>
          </div>

          {/* Selection toolbar */}
          <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-gray-light/40 rounded-lg border border-gray-light">
            <span className="text-body-sm text-gray-medium font-medium">Select for disavow:</span>
            <button
              onClick={selectAll}
              className="text-body-sm text-coral hover:underline flex items-center gap-1"
            >
              <CheckSquare size={14} />
              All ({backlinks.length})
            </button>
            <button
              onClick={selectHighRisk}
              className="text-body-sm text-coral hover:underline flex items-center gap-1"
            >
              <CheckSquare size={14} />
              High risk only ({riskCounts.high})
            </button>
            {selectedUrls.size > 0 && (
              <button
                onClick={deselectAll}
                className="text-body-sm text-gray-medium hover:underline flex items-center gap-1"
              >
                <Square size={14} />
                Clear selection
              </button>
            )}

            {selectedUrls.size > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-body-sm font-medium text-dark-text">
                  {selectedUrls.size} selected
                </span>
                <select
                  value={disavowMode}
                  onChange={(e) => setDisavowMode(e.target.value as DisavowMode)}
                  className="appearance-none bg-white border border-gray-200 rounded px-2 py-1 text-body-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
                  title="Choose whether to disavow specific URLs or entire domains"
                >
                  <option value="domain">By domain</option>
                  <option value="url">By URL</option>
                </select>
                <button
                  onClick={downloadDisavowFile}
                  className="btn-primary text-sm flex items-center gap-1.5 py-1.5 px-3"
                >
                  <Download size={14} />
                  Download Disavow File
                </button>
              </div>
            )}
          </div>

          {groupBy === 'risk' && (
            <div className="space-y-6">
              {(['high', 'medium', 'low'] as RiskLevel[]).map((level) => {
                const links = linksByRisk[level];
                if (links.length === 0) return null;
                const config = getRiskConfig(level);
                return (
                  <div key={level}>
                    <h4 className="text-body font-semibold text-gray-medium mb-3 flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                        {config.label}
                      </span>
                      <span className="text-body-sm font-normal">({links.length} backlinks)</span>
                    </h4>
                    <div className="space-y-3">
                      {links.map((link, idx) => (
                        <BacklinkCard
                          key={`${link.linkingUrl}-${idx}`}
                          backlink={link}
                          isSelected={selectedUrls.has(link.linkingUrl)}
                          onToggle={toggleUrl}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {groupBy === 'flag' && (
            <div className="space-y-6">
              {Array.from(linksByFlag.entries())
                .sort((a, b) => b[1].length - a[1].length)
                .map(([flag, links]) => {
                  const config = FLAG_CONFIG[flag];
                  return (
                    <div key={flag}>
                      <h4 className="text-body font-semibold text-gray-medium mb-3 flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                          {config.label}
                        </span>
                        <span className="text-body-sm font-normal">({links.length} backlinks)</span>
                      </h4>
                      <div className="space-y-3">
                        {links.map((link, idx) => (
                          <BacklinkCard
                            key={`${link.linkingUrl}-${idx}`}
                            backlink={link}
                            isSelected={selectedUrls.has(link.linkingUrl)}
                            onToggle={toggleUrl}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {groupBy === 'domain' && (
            <div className="space-y-6">
              {Array.from(linksByDomain.entries()).map(([domain, links]) => (
                <div key={domain}>
                  <h4 className="text-body font-semibold text-gray-medium mb-3 flex items-center gap-2">
                    <Globe size={16} />
                    <span className="text-dark-text">{domain}</span>
                    <span className="text-body-sm font-normal">({links.length} backlinks)</span>
                  </h4>
                  <div className="space-y-3">
                    {links.map((link, idx) => (
                      <BacklinkCard
                        key={`${link.linkingUrl}-${idx}`}
                        backlink={link}
                        isSelected={selectedUrls.has(link.linkingUrl)}
                        onToggle={toggleUrl}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {groupBy === 'none' && (
            <div className="space-y-3">
              {backlinks.map((link, idx) => (
                <BacklinkCard
                  key={`${link.linkingUrl}-${idx}`}
                  backlink={link}
                  isSelected={selectedUrls.has(link.linkingUrl)}
                  onToggle={toggleUrl}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
